/**
 * Choices and limits of the Service Desk steps (M27 S1, scope 14.3). Mirrors backend
 * `app/core/constants/workflow_service_steps.py`; the e2e spec saves and publishes a workflow with every choice, so
 * a value the server does not know fails there.
 */
import type { TicketPriority } from "@sagot/constants";

export type { TicketPriority };

/** What `update_ticket` and `create_ticket` may set a priority to (the same four as the ticket itself). */
export const STEP_PRIORITIES = ["low", "normal", "high", "urgent"] as const satisfies readonly TicketPriority[];

/** How `assign_ticket` picks the person: a routing method inside a team, a named member, or nobody. */
export const TICKET_ASSIGN_METHODS = ["round_robin", "least_open", "skills_match", "user", "unassign"] as const;
export type TicketAssignMethod = (typeof TICKET_ASSIGN_METHODS)[number];
/** The methods that pick a person out of a team (they need the team). */
export const ASSIGN_PICKS_IN_TEAM: ReadonlySet<TicketAssignMethod> = new Set(["round_robin", "least_open", "skills_match"]);

/** Who a `reply_to_ticket` message is written as: the "{workspace} Service Desk" system author, or a chosen member. */
export const REPLY_AUTHORS = ["system", "user"] as const;
export type ReplyAuthor = (typeof REPLY_AUTHORS)[number];

/**
 * Who `notify_contact` tells. Approvers and the visit contact belong to the orders and field-service phases; they are
 * accepted so a workflow written today keeps working, and resolve to nobody until then.
 */
export const CONTACT_AUDIENCES = ["requester", "participants", "org_admins", "org_approvers", "visit_contact"] as const;
export type ContactAudience = (typeof CONTACT_AUDIENCES)[number];
/** Audiences that exist in this release; the others are listed with a note. */
export const LIVE_CONTACT_AUDIENCES: ReadonlySet<ContactAudience> = new Set(["requester", "participants", "org_admins"]);

/** How `notify_contact` reaches a contact: e-mail, a line in the chat they have open, or Messenger inside its 24-hour window. */
export const CONTACT_CHANNELS = ["email", "in_chat", "messenger"] as const;
export type ContactChannel = (typeof CONTACT_CHANNELS)[number];

/** The client e-mails a step can send (scope 11.3). */
export const CLIENT_TEMPLATES = ["ticket_received", "ticket_reply", "ticket_status", "ticket_waiting_reminder", "ticket_resolved"] as const;
export type ClientTemplate = (typeof CLIENT_TEMPLATES)[number];

/** Where `link_ticket` finds the ticket: the one the event is about, or the one a run variable names. */
export const LINK_SOURCES = ["event", "variable"] as const;

/** The run variables `create_ticket` sets for the steps after it. */
export const CREATE_TICKET_VARIABLES = ["var.ticket_number", "var.ticket_id"] as const;
export const DEFAULT_LINK_VARIABLE = "var.ticket_number";

/** The most `reply_to_ticket` messages one run writes to one ticket (`REPLY_TO_TICKET_CAP_PER_RUN` in `@sagot/constants`). */
export const SERVICE_STEP_LIMITS = {
  title: 400,
  text: 4_000,
  resolutionNote: 1_000,
  fields: 20,
  fieldTemplate: 500,
  mentions: 10,
  skills: 10,
  skill: 40,
  dueInMinutesMax: 60 * 24 * 365,
  audiences: 5,
  channels: 3,
  tags: 10,
  tag: 40,
} as const;

/** `set_ticket_state` may name any status the workspace has; these are the fixed categories that carry behaviour. */
export const RESOLVING_STATUS_CATEGORIES: ReadonlySet<string> = new Set(["resolved", "closed"]);

/** A status key, a resolution code and a custom field key (backend `Key`). */
export const SERVICE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,59}$/;
